import nacl from "tweetnacl";

const enc = new TextEncoder();
const dec = new TextDecoder();

export const toB64 = (u) => {
  let s = "";
  for (let i = 0; i < u.length; i += 0x8000) s += String.fromCharCode.apply(null, u.subarray(i, i + 0x8000));
  return btoa(s);
};
export const fromB64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
const toUrl = (s) => s.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const fromUrl = (s) => {
  let t = s.replace(/-/g, "+").replace(/_/g, "/");
  while (t.length % 4) t += "=";
  return t;
};
const concat = (a, b) => {
  const out = new Uint8Array(a.length + b.length);
  out.set(a);
  out.set(b, a.length);
  return out;
};

export function newIdentity() {
  const s = nacl.sign.keyPair();
  const b = nacl.box.keyPair();
  return { sign_pk: toB64(s.publicKey), sign_sk: toB64(s.secretKey), box_pk: toB64(b.publicKey), box_sk: toB64(b.secretKey) };
}

export function identityFromSecrets(signSkB64, boxSkB64) {
  const s = nacl.sign.keyPair.fromSecretKey(fromB64(signSkB64));
  const b = nacl.box.keyPair.fromSecretKey(fromB64(boxSkB64));
  return { sign_pk: toB64(s.publicKey), sign_sk: toB64(s.secretKey), box_pk: toB64(b.publicKey), box_sk: toB64(b.secretKey) };
}

export const toCode = (signPk, boxPk) => "aph1" + toUrl(toB64(concat(fromB64(signPk), fromB64(boxPk))));

export function parseCode(code) {
  const c = (code || "").trim();
  if (!c.startsWith("aph1")) return null;
  try {
    const raw = fromB64(fromUrl(c.slice(4)));
    if (raw.length !== 64) return null;
    return { sign_pk: toB64(raw.slice(0, 32)), box_pk: toB64(raw.slice(32)) };
  } catch {
    return null;
  }
}

export async function sha256Hex(str) {
  const h = new Uint8Array(await crypto.subtle.digest("SHA-256", enc.encode(str)));
  return Array.from(h, (x) => x.toString(16).padStart(2, "0")).join("");
}

export const routingId = async (signPk) => (await sha256Hex(`aph-route:${signPk}`)).slice(0, 40);
export const directSid = async (a, b) => "d_" + (await sha256Hex([a, b].sort().join("|"))).slice(0, 24);
export const sharedSecret = (theirBoxPk, myBoxSk) => toB64(nacl.box.before(fromB64(theirBoxPk), fromB64(myBoxSk)));
export const signChallenge = (nonceB64, signSk) => toB64(nacl.sign.detached(fromB64(nonceB64), fromB64(signSk)));

export async function safetyNumber(codes) {
  const h = await sha256Hex([...codes].sort().join("|"));
  return Array.from({ length: 12 }, (_, i) => String(parseInt(h.slice(i * 5, i * 5 + 5), 16) % 100000).padStart(5, "0"));
}

function padTo(bytes, block) {
  const out = nacl.randomBytes(Math.ceil((bytes.length + 4) / block) * block);
  new DataView(out.buffer).setUint32(0, bytes.length);
  out.set(bytes, 4);
  return out;
}

const unpad = (b) => b.slice(4, 4 + new DataView(b.buffer, b.byteOffset).getUint32(0));

export function sealFor(recipientBoxPk, body, me) {
  const b = JSON.stringify(body);
  const sig = nacl.sign.detached(enc.encode(b), fromB64(me.sign_sk));
  const inner = padTo(enc.encode(JSON.stringify({ b, s: toB64(sig) })), 256);
  const eph = nacl.box.keyPair();
  const nonce = nacl.randomBytes(24);
  const ct = nacl.box(inner, nonce, fromB64(recipientBoxPk), eph.secretKey);
  return { encryptedPayload: toB64(concat(nonce, ct)), ephemeralSenderKey: toB64(eph.publicKey) };
}

export function openSealed(payload, ephKey, me) {
  const raw = fromB64(payload);
  const pt = nacl.box.open(raw.slice(24), raw.slice(0, 24), fromB64(ephKey), fromB64(me.box_sk));
  if (!pt) return null;
  const inner = JSON.parse(dec.decode(unpad(pt)));
  const body = JSON.parse(inner.b);
  const from = parseCode(body.from);
  if (!from) return null;
  const valid = nacl.sign.detached.verify(enc.encode(inner.b), fromB64(inner.s), fromB64(from.sign_pk));
  return { body, valid };
}

export function encryptFile(bytes) {
  const key = nacl.randomBytes(32);
  const nonce = nacl.randomBytes(24);
  return { ct: nacl.secretbox(bytes, nonce, key), key: toB64(key), nonce: toB64(nonce) };
}

export const decryptFile = (ct, key, nonce) => nacl.secretbox.open(ct, fromB64(nonce), fromB64(key));
